import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, Users, Globe, MapPin, TrendingUp, Eye } from "lucide-react";

export const AnalyticsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/analytics")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-rb-border p-8 mb-8 flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-7 h-7 animate-spin text-rb-red" />
      </div>
    );
  }
  if (!data) return null;

  const maxDaily = Math.max(1, ...data.daily_last_7.map((d) => d.count));
  const maxCountry = Math.max(1, ...data.top_countries.map((c) => c.count));

  return (
    <section data-testid="analytics-panel" className="mb-10">
      <div className="flex items-center gap-3 mb-5">
        <span className="h-[2px] w-8 bg-rb-red" />
        <h2 className="font-heading text-lg font-bold text-rb-ink">إحصائيات الموقع</h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Tile icon={Eye} label="إجمالي الزيارات" value={data.total} testid="ana-total" />
        <Tile icon={TrendingUp} label="اليوم" value={data.today} testid="ana-today" accent />
        <Tile icon={Users} label="آخر ٧ أيام" value={data.week} testid="ana-week" />
        <Tile icon={Globe} label="زوّار فريدون (٣٠ يوم)" value={data.unique_ips_30d} testid="ana-unique" />
      </div>

      <div className="grid lg:grid-cols-3 gap-3">
        {/* Daily chart */}
        <div className="lg:col-span-2 bg-white border border-rb-border p-5 shadow-sm" data-testid="daily-chart">
          <h3 className="text-xs tracking-[0.25em] text-rb-textMuted uppercase mb-5">
            الزيارات — آخر ٧ أيام
          </h3>
          <div className="flex items-end justify-between h-44 gap-2">
            {data.daily_last_7.map((d) => {
              const h = (d.count / maxDaily) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-2 min-w-0">
                  <div className="text-xs font-bold text-rb-ink tabular-nums">{d.count}</div>
                  <div className="w-full bg-rb-surface relative" style={{ height: "100%" }}>
                    <div
                      className="absolute bottom-0 inset-x-0 bg-rb-red transition-all"
                      style={{ height: `${h}%`, minHeight: d.count > 0 ? "3px" : "0" }}
                      title={`${d.date}: ${d.count}`}
                    />
                  </div>
                  <div className="text-[10px] text-rb-textMuted truncate w-full text-center">
                    {d.date.slice(5)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top countries */}
        <div className="bg-white border border-rb-border p-5 shadow-sm" data-testid="top-countries">
          <h3 className="text-xs tracking-[0.25em] text-rb-textMuted uppercase mb-5 flex items-center gap-2">
            <Globe className="w-3.5 h-3.5 text-rb-red" />
            أكثر الدول زيارة
          </h3>
          {data.top_countries.length === 0 ? (
            <p className="text-xs text-rb-textMuted">لا توجد بيانات بعد</p>
          ) : (
            <ul className="space-y-3">
              {data.top_countries.slice(0, 6).map((c) => {
                const w = (c.count / maxCountry) * 100;
                return (
                  <li key={(c.country_code || "") + c.country} className="text-xs">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-bold text-rb-ink flex items-center gap-2">
                        {c.country_code && (
                          <span className="font-mono bg-rb-surface px-1.5 py-0.5 text-[10px]">
                            {c.country_code}
                          </span>
                        )}
                        {c.country}
                      </span>
                      <span className="font-mono text-rb-textMuted tabular-nums">{c.count}</span>
                    </div>
                    <div className="h-1 bg-rb-surface">
                      <div className="h-full bg-rb-red" style={{ width: `${w}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Top cities */}
      {data.top_cities.length > 0 && (
        <div className="bg-white border border-rb-border p-5 shadow-sm mt-3" data-testid="top-cities">
          <h3 className="text-xs tracking-[0.25em] text-rb-textMuted uppercase mb-5 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-rb-red" />
            أكثر المدن زيارة
          </h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
            {data.top_cities.slice(0, 9).map((c, i) => (
              <div key={i} className="flex items-center justify-between bg-rb-surface px-3 py-2 border border-rb-border">
                <div className="text-xs">
                  <div className="font-bold text-rb-ink">{c.city}</div>
                  <div className="text-rb-textMuted text-[10px]">{c.country}</div>
                </div>
                <div className="font-mono text-sm font-bold text-rb-red tabular-nums">{c.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

const Tile = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className={`bg-white border p-4 shadow-sm ${accent ? "border-rb-red" : "border-rb-border"}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${accent ? "text-rb-red" : "text-rb-textMuted"}`} />
      <div className="text-[10px] tracking-[0.25em] text-rb-textMuted uppercase">{label}</div>
    </div>
    <div className={`font-heading text-3xl font-extrabold tabular-nums ${accent ? "text-rb-red" : "text-rb-ink"}`}>
      {value.toLocaleString("en-US")}
    </div>
  </div>
);
